/**
 * Graphiques en SVG ecrits a la main.
 *
 * Aucune bibliotheque de visualisation : la politique de securite du site
 * interdit tout script tiers et l'application doit fonctionner integralement
 * hors ligne. Le SVG inline satisfait les deux sans peser un octet de plus.
 *
 * ## Regles suivies (reprises de perso2)
 *
 * - marques fines, extremites arrondies a 4 px ancrees a la ligne de base ;
 * - 2 px de fond entre deux marques voisines, jamais de contour pour separer ;
 * - grille et axes en filets pleins, jamais en pointilles ;
 * - pas d'etiquette sur chaque barre : seuls les reperes utiles sont annotes ;
 * - **chaque graphique possede son equivalent en tableau**, car une infobulle
 *   ne doit jamais etre le seul moyen de lire une valeur — et il n'y a pas de
 *   survol sur un ecran tactile.
 *
 * Les couleurs `--viz-depense` / `--viz-revenu` sont la paire deja validee
 * pour perso2 (clarte, chroma, separation en vision deficiente, contraste).
 */

import { useState, type ReactNode } from 'react'

import { formatMontant, formatNombre } from '../domain/argent'
import type { Cents } from '../domain/types'

export function Figure({
  titre,
  legende,
  tableau,
  children,
}: {
  titre: string
  legende?: ReactNode
  tableau: ReactNode
  children: ReactNode
}) {
  const [vueTableau, setVueTableau] = useState(false)
  return (
    <div className="figure">
      <div className="figure__entete">
        <h2>{titre}</h2>
        <button
          type="button"
          className="btn btn--fantome btn--petit"
          aria-pressed={vueTableau}
          onClick={() => setVueTableau((v) => !v)}
        >
          {vueTableau ? 'Graphique' : 'Chiffres'}
        </button>
      </div>
      {vueTableau ? <div className="defilable">{tableau}</div> : children}
      {!vueTableau && legende}
    </div>
  )
}

export function Legende({ series }: { series: { libelle: string; couleur: string }[] }) {
  return (
    <div className="ligne ligne--enroulee tres-doux">
      {series.map((s) => (
        <span key={s.libelle} className="ligne" style={{ gap: 5 }}>
          <span className="pastille" style={{ background: s.couleur, borderRadius: 3 }} />
          {s.libelle}
        </span>
      ))}
    </div>
  )
}

/** Barre a sommet arrondi, ancree a la ligne de base. */
function cheminBarre(x: number, y: number, l: number, h: number): string {
  if (h <= 0) return ''
  const r = Math.min(4, l / 2, h)
  return `M${x},${y + h}L${x},${y + r}Q${x},${y} ${x + r},${y}L${x + l - r},${y}Q${x + l},${y} ${x + l},${y + r}L${x + l},${y + h}Z`
}

/** Graduation « ronde » : 1, 2 ou 5 × 10^n francs. */
function pasRond(max: number, cible = 4): number {
  const brut = max / cible
  const p = 10 ** Math.floor(Math.log10(Math.max(brut, 1)))
  for (const m of [1, 2, 5, 10]) if (m * p >= brut) return m * p
  return 10 * p
}

/** Francs abreges pour un axe : « 2k », « 500 ». */
function tickFrancs(c: Cents): string {
  const f = c / 100
  if (Math.abs(f) >= 1000) return `${Math.round(f / 100) / 10}k`.replace('.0k', 'k')
  return String(Math.round(f))
}

export interface GroupeBarres {
  libelle: string
  valeurs: Cents[]
  info?: string
}

/**
 * Barres groupees verticales : une couleur par serie (au plus deux), une
 * graduation en francs. Une ligne de reference optionnelle (budget) est tracee
 * en filet plein.
 */
export function BarresGroupees({
  groupes,
  couleurs,
  reference,
  etiquette,
}: {
  groupes: GroupeBarres[]
  couleurs: string[]
  reference?: Cents | null
  etiquette: string
}) {
  const largeur = 320
  const hauteur = 150
  const margeBas = 16
  const margeGauche = 30
  const margeHaut = 6
  const hPlot = hauteur - margeBas
  const largeurPlot = largeur - margeGauche - 4

  const max = Math.max(1, reference ?? 0, ...groupes.flatMap((g) => g.valeurs))
  const pas = pasRond(max)
  const plafond = Math.ceil(max / pas) * pas
  const y = (v: number) => hPlot - (v / plafond) * (hPlot - margeHaut)

  const pasGroupe = largeurPlot / Math.max(1, groupes.length)
  const nSeries = Math.max(1, couleurs.length)
  const largeurBarre = Math.max(2, (pasGroupe - 4) / nSeries - 2)

  const graduations: number[] = []
  for (let v = 0; v <= plafond; v += pas) graduations.push(v)

  return (
    <svg className="viz" viewBox={`0 0 ${largeur} ${hauteur}`} role="img" aria-label={etiquette}>
      {graduations.map((v) => (
        <g key={v}>
          <line className={v === 0 ? 'viz__axe' : 'viz__grille'} x1={margeGauche} y1={y(v)} x2={largeur - 4} y2={y(v)} />
          <text className="viz__tick" x={margeGauche - 4} y={y(v) + 3} textAnchor="end">
            {tickFrancs(v)}
          </text>
        </g>
      ))}

      {groupes.map((g, k) => {
        const x0 = margeGauche + k * pasGroupe + 2
        return (
          <g key={g.libelle}>
            {g.valeurs.map((v, s) => {
              const x = x0 + s * (largeurBarre + 2)
              return <path key={s} d={cheminBarre(x, y(Math.max(0, v)), largeurBarre, hPlot - y(Math.max(0, v)))} fill={couleurs[s]} />
            })}
            <rect x={x0 - 2} y={0} width={pasGroupe} height={hPlot} fill="transparent">
              <title>{g.info ?? `${g.libelle} : ${g.valeurs.map(formatMontant).join(' / ')}`}</title>
            </rect>
            <text className="viz__tick" x={x0 + (nSeries * (largeurBarre + 2)) / 2 - 1} y={hauteur - 4} textAnchor="middle">
              {g.libelle}
            </text>
          </g>
        )
      })}

      {reference != null && reference > 0 && (
        <line className="viz__objectif" x1={margeGauche} y1={y(reference)} x2={largeur - 4} y2={y(reference)} />
      )}
    </svg>
  )
}

/**
 * Barres horizontales par categorie. En divs plutot qu'en SVG : le texte s'y
 * aligne naturellement sur des libelles de longueur variable.
 */
export function BarresHorizontales({
  lignes,
}: {
  lignes: { cle: string; libelle: ReactNode; valeur: Cents; detail?: string; couleur?: string }[]
}) {
  const max = Math.max(1, ...lignes.map((l) => l.valeur))
  return (
    <div className="pile pile--serree">
      {lignes.map((l) => (
        <div key={l.cle}>
          <div className="ligne ligne--espacee">
            <span className="doux">{l.libelle}</span>
            <span className="doux mono">
              {formatNombre(l.valeur)}
              {l.detail && <span className="tres-doux"> · {l.detail}</span>}
            </span>
          </div>
          <div className="jauge">
            <div className="jauge__part" style={{ width: `${(Math.max(0, l.valeur) / max) * 100}%`, background: l.couleur }} />
          </div>
        </div>
      ))}
    </div>
  )
}
